import {
    Address,
    beginCell,
    Cell,
    contractAddress,
    storeStateInit,
    toNano,
} from '@ton/core';

export const TESTNET_CHAIN = '-3';
export const MAINNET_CHAIN = '-239';
export const DEPLOY_VALUE = toNano('0.10');
export const CONTROL_VALUE = toNano('0.05');
export const CLAIM_VALUE = toNano('0.10');

const OPS = {
    setIssuanceEnabled: 0x4b414501,
    setK17Recipient: 0x4b313701,
    claimK17: 0x4b313743,
    claimK18: 0x4b313843,
} as const;

type CompiledArtifact = { hex: string; hash: string; hashBase64: string };

export type PublicProtocol = {
    schema: string;
    canonical_pair: string;
    hash_algorithm: 'SHA-256';
    pair_root_sha256: string;
    k17_public_identity_sha256: string;
    k18_public_identity_sha256: string;
};

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return window.btoa(binary);
}

function hex256(value: string, field: string): bigint {
    if (!/^[a-f0-9]{64}$/i.test(value)) throw new Error(`Некорректный ${field}`);
    return BigInt(`0x${value}`);
}

async function loadCode(name: 'KaemilithCollection' | 'KaemilithItem'): Promise<Cell> {
    const response = await fetch(`/contracts/${name}.compiled.json`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Не найден код контракта ${name}`);
    const artifact = (await response.json()) as CompiledArtifact;
    return Cell.fromHex(artifact.hex);
}

async function loadProtocol(): Promise<PublicProtocol> {
    const response = await fetch('/protocol.json', { cache: 'no-store' });
    if (!response.ok) throw new Error('Не найден публичный протокол K17/K18');
    const protocol = (await response.json()) as PublicProtocol;
    hex256(protocol.pair_root_sha256, 'pair root');
    hex256(protocol.k17_public_identity_sha256, 'K17 identity hash');
    hex256(protocol.k18_public_identity_sha256, 'K18 identity hash');
    return protocol;
}

function collectionData(
    admin: Address,
    itemCode: Cell,
    collectionMetadataUri: string,
    commonMetadataUri: string,
    protocol: PublicProtocol,
): Cell {
    const content = beginCell()
        .storeRef(beginCell().storeUint(1, 8).storeStringRefTail(collectionMetadataUri).endCell())
        .storeRef(beginCell().storeStringTail(commonMetadataUri).endCell())
        .endCell();

    const zeroRoyalty = beginCell()
        .storeUint(0, 16)
        .storeUint(100, 16)
        .storeAddress(admin)
        .endCell();

    const contentHashes = beginCell()
        .storeUint(hex256(protocol.k17_public_identity_sha256, 'K17 identity hash'), 256)
        .storeUint(hex256(protocol.k18_public_identity_sha256, 'K18 identity hash'), 256)
        .endCell();

    const protocolCell = beginCell()
        .storeUint(hex256(protocol.pair_root_sha256, 'pair root'), 256)
        .storeRef(contentHashes)
        .storeAddress(null)
        .storeBit(false)
        .storeBit(false)
        .storeBit(false)
        .endCell();

    return beginCell()
        .storeAddress(admin)
        .storeUint(18, 64)
        .storeRef(content)
        .storeRef(itemCode)
        .storeRef(zeroRoyalty)
        .storeRef(protocolCell)
        .endCell();
}

export type PreparedCollection = {
    address: Address;
    stateInitBase64: string;
    protocol: PublicProtocol;
};

export async function prepareCollection(adminAddress: string, publicBaseUrl: string): Promise<PreparedCollection> {
    const admin = Address.parse(adminAddress);
    const [collectionCode, itemCode, protocol] = await Promise.all([
        loadCode('KaemilithCollection'),
        loadCode('KaemilithItem'),
        loadProtocol(),
    ]);
    const base = publicBaseUrl.replace(/\/$/, '');
    const data = collectionData(
        admin,
        itemCode,
        `${base}/metadata/collection.json`,
        `${base}/metadata/`,
        protocol,
    );
    const init = { code: collectionCode, data };
    const address = contractAddress(0, init);
    const stateInit = beginCell().store(storeStateInit(init)).endCell();
    return { address, stateInitBase64: bytesToBase64(stateInit.toBoc()), protocol };
}

function queryOnlyPayload(op: number): string {
    return bytesToBase64(beginCell().storeUint(op, 32).storeUint(0, 64).endCell().toBoc());
}

export function setIssuancePayload(enabled: boolean): string {
    const body = beginCell()
        .storeUint(OPS.setIssuanceEnabled, 32)
        .storeUint(0, 64)
        .storeBit(enabled)
        .endCell();
    return bytesToBase64(body.toBoc());
}

export function setK17RecipientPayload(recipientAddress: string): string {
    const recipient = Address.parse(recipientAddress);
    const body = beginCell()
        .storeUint(OPS.setK17Recipient, 32)
        .storeUint(0, 64)
        .storeAddress(recipient)
        .endCell();
    return bytesToBase64(body.toBoc());
}

export function claimArtifactPayload(artifact: 'k17' | 'k18'): string {
    return queryOnlyPayload(artifact === 'k17' ? OPS.claimK17 : OPS.claimK18);
}

export function sameAddress(a: string | undefined, b: string | undefined): boolean {
    if (!a || !b) return false;
    try {
        return Address.parse(a).equals(Address.parse(b));
    } catch {
        return false;
    }
}

export function shortAddress(address?: string): string {
    if (!address) return '—';
    try {
        const friendly = Address.parse(address).toString({ testOnly: true, bounceable: false });
        return `${friendly.slice(0, 7)}…${friendly.slice(-6)}`;
    } catch {
        return `${address.slice(0, 7)}…${address.slice(-6)}`;
    }
}

export function shortHash(hash?: string): string {
    if (!hash) return '—';
    return `${hash.slice(0, 8)}…${hash.slice(-8)}`;
}
